import React, { useCallback, useEffect, useRef, useState } from "react";
import { MessageCircle, Send, X } from "lucide-react";
import { useI18n } from "@/i18n";
import { Button } from "@/components/ui/button";

export const ASK_VYTALI_OPEN = "ask-vytali:open";
export const ASK_VYTALI_CLOSE = "ask-vytali:close";
export const openAskVytali = () => window.dispatchEvent(new CustomEvent(ASK_VYTALI_OPEN));

const API = `${process.env.REACT_APP_BACKEND_URL}/api/public/livechat`;
const SESSION_KEY = "ask-vytali-session";
const URL_RE = /(https?:\/\/[^\s)]+)/g;
const isUrl = (chunk) => /^https?:\/\//.test(chunk);

function Rich({ text }) {
  return text.split(URL_RE).map((chunk, index) =>
    isUrl(chunk) ? (
      <a key={index} href={chunk} className="break-all underline underline-offset-2">
        {chunk.replace(/^https?:\/\//, "")}
      </a>
    ) : (
      <React.Fragment key={index}>{chunk}</React.Fragment>
    ),
  );
}

export default function AskVytali() {
  const { t, lang } = useI18n();
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const scroller = useRef(null);
  const inputRef = useRef(null);
  const launcherRef = useRef(null);
  const closeRef = useRef(null);

  /** The DB session is created on the first visitor message, never on panel open. */
  const start = useCallback(async () => {
    const stored = sessionStorage.getItem(SESSION_KEY);
    if (stored) {
      setSessionId(stored);
      return stored;
    }
    try {
      const res = await fetch(`${API}/session`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ language: lang, page_path: window.location.pathname }),
      });
      if (!res.ok) throw new Error("session");
      const data = await res.json();
      sessionStorage.setItem(SESSION_KEY, data.session_id);
      setSessionId(data.session_id);
      setFailed(false);
      return data.session_id;
    } catch {
      setFailed(true);
      return null;
    }
  }, [lang]);

  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener(ASK_VYTALI_OPEN, handler);
    return () => window.removeEventListener(ASK_VYTALI_OPEN, handler);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    setMessages((prev) => (prev.length ? prev : [{ role: "assistant", content: t("chat.greeting") }]));
    const focusId = setTimeout(() => inputRef.current?.focus(), 150);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (event) => {
      if (event.key === "Escape") closeRef.current?.();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      clearTimeout(focusId);
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, t]);

  const wasOpen = useRef(false);
  useEffect(() => {
    if (!open && wasOpen.current) launcherRef.current?.focus();
    wasOpen.current = open;
  }, [open]);

  useEffect(() => {
    if (scroller.current) scroller.current.scrollTop = scroller.current.scrollHeight;
  }, [messages, busy]);

  const send = async (event) => {
    event?.preventDefault();
    const text = draft.trim();
    if (!text || busy) return;
    const id = sessionId || (await start());
    if (!id) return;
    setDraft("");
    setMessages((prev) => [...prev, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setBusy(true);
    try {
      const res = await fetch(`${API}/message`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: id, message: text, language: lang }),
      });
      if (!res.ok || !res.body) throw new Error("stream");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop() || "";
        for (const part of parts) {
          const line = part.replace(/^data: /, "").trim();
          if (!line) continue;
          const payload = JSON.parse(line);
          if (payload.delta) {
            setMessages((prev) => {
              const next = [...prev];
              next[next.length - 1] = {
                role: "assistant",
                content: next[next.length - 1].content + payload.delta,
              };
              return next;
            });
          }
        }
      }
      setMessages((prev) => {
        if (prev[prev.length - 1]?.content) return prev;
        const next = [...prev];
        next[next.length - 1] = { role: "assistant", content: t("chat.error") };
        return next;
      });
    } catch {
      setMessages((prev) => {
        const next = [...prev];
        next[next.length - 1] = { role: "assistant", content: t("chat.error") };
        return next;
      });
    } finally {
      setBusy(false);
    }
  };

  const close = () => {
    setOpen(false);
    window.dispatchEvent(new CustomEvent(ASK_VYTALI_CLOSE));
    if (sessionId) {
      fetch(`${API}/close`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId }),
      }).catch(() => {});
    }
  };
  closeRef.current = close;

  return (
    <>
      {!open && (
        <button
          type="button"
          ref={launcherRef}
          data-testid="ask-vytali-launcher"
          onClick={() => setOpen(true)}
          aria-label={t("chat.open")}
          className="fixed bottom-4 right-4 z-50 hidden items-center gap-2 rounded-full bg-accent px-5 py-3 text-sm font-medium text-accent-foreground shadow-lg transition-transform duration-200 hover:scale-105 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-foreground sm:bottom-6 sm:right-6 sm:flex"
        >
          <MessageCircle className="h-4 w-4" aria-hidden="true" />
          Ask Vytali
        </button>
      )}

      {open && (
        <section
          data-testid="ask-vytali-panel"
          role="dialog"
          aria-modal="true"
          aria-label="Ask Vytali"
          className="ask-vytali-sheet fixed inset-x-0 bottom-0 z-50 box-border flex w-full max-w-[100vw] flex-col overflow-hidden bg-surface shadow-2xl sm:inset-x-auto sm:bottom-6 sm:right-6 sm:w-[380px] sm:rounded-lg sm:border sm:border-border"
        >
          <header
            className="flex shrink-0 items-start justify-between gap-3 border-b border-border bg-primary px-4 py-3 text-primary-foreground sm:rounded-t-lg"
            style={{ paddingTop: "calc(0.75rem + env(safe-area-inset-top, 0px))" }}
          >
            <div className="min-w-0">
              <p className="text-sm font-medium">Ask Vytali</p>
              <p className="text-xs text-primary-foreground/60">{t("chat.subtitle")}</p>
            </div>
            <button
              type="button"
              data-testid="ask-vytali-close"
              onClick={close}
              aria-label={t("chat.close")}
              className="-mr-1 flex h-11 w-11 shrink-0 items-center justify-center rounded-full transition-colors hover:bg-primary-foreground/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent sm:h-9 sm:w-9"
            >
              <X className="h-5 w-5" aria-hidden="true" />
            </button>
          </header>

          <div ref={scroller} data-testid="ask-vytali-messages" className="min-h-0 flex-1 space-y-3 overflow-y-auto overflow-x-hidden overscroll-contain px-4 py-4">
            {failed && (
              <p data-testid="ask-vytali-unavailable" className="text-sm text-destructive">
                {t("chat.error")}
              </p>
            )}
            {messages.map((m, index) => (
              <div
                key={index}
                data-testid={m.role === "user" ? "ask-vytali-user-message" : "ask-vytali-bot-message"}
                className={`max-w-[85%] whitespace-pre-wrap break-words px-3 py-2 text-sm leading-relaxed ${
                  m.role === "user"
                    ? "ml-auto bg-accent text-accent-foreground"
                    : "bg-secondary text-foreground"
                }`}
              >
                {m.content ? <Rich text={m.content} /> : busy ? t("chat.typing") : ""}
              </div>
            ))}
          </div>

          <div className="shrink-0 border-t border-border" style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}>
            <form onSubmit={send} className="flex items-center gap-2 px-3 py-3">
              <input
                ref={inputRef}
                data-testid="ask-vytali-input"
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                placeholder={t("chat.placeholder")}
                aria-label={t("chat.placeholder")}
                maxLength={2000}
                enterKeyHint="send"
                autoComplete="off"
                className="h-11 min-w-0 flex-1 border border-input bg-background px-3 text-[16px] leading-none focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              />
              <Button
                type="submit"
                data-testid="ask-vytali-send"
                disabled={busy || !draft.trim()}
                aria-label={t("chat.send")}
                className="h-11 w-11 shrink-0 rounded-full bg-accent p-0 text-accent-foreground hover:bg-accent/90"
              >
                <Send className="h-5 w-5" aria-hidden="true" />
              </Button>
            </form>
            <p className="px-4 pb-3 text-[11px] leading-snug text-muted-foreground">{t("chat.disclaimer")}</p>
          </div>
        </section>
      )}
    </>
  );
}
